import { z } from "zod";

import { projectPersonaAwarenessSchema } from "./projectContext";
import { sightglassAdTypeSchema } from "./sightglassApi";
import {
  sightglassAgeBandSchema,
  sightglassAngleTypeSchema,
  sightglassHookTypeSchema,
  sightglassOfferTagSchema,
  sightglassPageTypeSchema,
} from "./sightglassOffers";

// Arena map: two axes of two poles each; the server scores items by cosine against high minus low pole embeddings.

/** Phrases a pole may carry: each costs one embedding. */
export const SIGHTGLASS_ARENA_PHRASES_PER_POLE = 3;

export const sightglassArenaPolesSchema = z
  .object({
    low: z.string().trim().min(1).max(60),
    high: z.string().trim().min(1).max(60),
    lowPhrases: z
      .array(z.string().trim().min(1).max(120))
      .min(1)
      .max(SIGHTGLASS_ARENA_PHRASES_PER_POLE),
    highPhrases: z
      .array(z.string().trim().min(1).max(120))
      .min(1)
      .max(SIGHTGLASS_ARENA_PHRASES_PER_POLE),
  })
  .describe("SightglassArenaPoles");
export type SightglassArenaPoles = z.infer<typeof sightglassArenaPolesSchema>;

export const sightglassArenaAxisKeySchema = z
  .enum(["Beginner", "Solo", "Feeling", "Need", "Cheap", "Classic", "Everyday"])
  .describe("SightglassArenaAxisKey");
export type SightglassArenaAxisKey = z.infer<
  typeof sightglassArenaAxisKeySchema
>;

export const SIGHTGLASS_ARENA_AXES: Record<
  SightglassArenaAxisKey,
  SightglassArenaPoles
> = {
  Beginner: {
    low: "Beginner",
    high: "Expert",
    lowPhrases: [
      "New to the category and just starting out",
      "Simple, easy and forgiving to use",
      "No experience or skill needed",
    ],
    highPhrases: [
      "Experienced enthusiasts who know the craft",
      "Professional-grade performance and control",
      "Advanced gear for serious users",
    ],
  },
  Solo: {
    low: "Solo",
    high: "Social",
    lowPhrases: [
      "A personal routine enjoyed alone",
      "Me-time and self-focus",
      "A quiet, private moment",
    ],
    highPhrases: [
      "Shared with friends and family",
      "Hosting and gathering people together",
      "Made for groups and togetherness",
    ],
  },
  Feeling: {
    low: "Feeling",
    high: "Logic",
    lowPhrases: [
      "Emotional and aspirational",
      "How it makes you feel",
      "Lifestyle, identity and belonging",
    ],
    highPhrases: [
      "Facts, specs and proof",
      "Rational comparison of value",
      "Measurable results and evidence",
    ],
  },
  Need: {
    low: "Need",
    high: "Treat",
    lowPhrases: [
      "Practical problem-solving",
      "Essential utility",
      "Functional necessity",
    ],
    highPhrases: [
      "Indulgent self-care",
      "Luxurious reward",
      "Pleasurable treat",
    ],
  },
  Cheap: {
    low: "Cheap",
    high: "Premium",
    lowPhrases: [
      "Affordable everyday price",
      "Budget-friendly value",
      "Deals, discounts and savings",
    ],
    highPhrases: [
      "Premium luxury price",
      "High-end investment piece",
      "Exclusive and upscale",
    ],
  },
  Classic: {
    low: "Classic",
    high: "Modern",
    lowPhrases: [
      "Timeless traditional design",
      "Heritage and old-world craftsmanship",
      "A familiar, vintage look",
    ],
    highPhrases: [
      "Sleek contemporary design",
      "New technology and innovation",
      "Minimal modern aesthetic",
    ],
  },
  Everyday: {
    low: "Everyday",
    high: "Special occasion",
    lowPhrases: [
      "Part of the daily routine",
      "Everyday essentials",
      "Regular weeknight use",
    ],
    highPhrases: [
      "Holidays and celebrations",
      "Gifting and special moments",
      "A once-a-year occasion",
    ],
  },
};

export const sightglassArenaLensKeySchema = z
  .enum(["who", "why", "price", "occasion"])
  .describe("SightglassArenaLensKey");
export type SightglassArenaLensKey = z.infer<
  typeof sightglassArenaLensKeySchema
>;

export const SIGHTGLASS_ARENA_LENSES: readonly {
  key: z.infer<typeof sightglassArenaLensKeySchema>;
  label: string;
  description: string;
  x: SightglassArenaAxisKey;
  y: SightglassArenaAxisKey;
}[] = [
  {
    key: "who",
    label: "Who it's for",
    description: "Experience level, alone or with others",
    x: "Beginner",
    y: "Solo",
  },
  {
    key: "why",
    label: "Why they buy",
    description: "Emotional or rational, need or treat",
    x: "Feeling",
    y: "Need",
  },
  {
    key: "price",
    label: "Price and style",
    description: "Price tier and look",
    x: "Cheap",
    y: "Classic",
  },
  {
    key: "occasion",
    label: "Occasion",
    description: "Daily use or special moment",
    x: "Everyday",
    y: "Solo",
  },
];

export const sightglassArenaKindSchema = z
  .enum(["personas", "ads", "pages", "companies"])
  .describe("SightglassArenaKind");
export type SightglassArenaKind = z.infer<typeof sightglassArenaKindSchema>;

export const sightglassArenaAxisBodySchema = z
  .object({
    low: z
      .string()
      .trim()
      .min(1)
      .max(60)
      .describe("One end of a custom axis, e.g. 'Minimal'"),
  })
  .strict()
  .describe("SightglassArenaAxisBody");

/** What the model writes for a custom axis; the caller's own word stays the low pole. */
export const sightglassArenaAxisSuggestionSchema = z
  .object({
    high: z
      .string()
      .describe("The opposite end, one or two words, e.g. 'Maximal'"),
    lowPhrases: z
      .array(z.string())
      .describe(
        "Three short brand-agnostic phrases a product or ad at the low end would evoke",
      ),
    highPhrases: z
      .array(z.string())
      .describe("Three short brand-agnostic phrases for the high end"),
  })
  .describe("SightglassArenaAxisSuggestion");

/** Narrows what is plotted. Each field applies to the kinds named in its description and is ignored for the others. */
export const sightglassArenaFiltersSchema = z
  .object({
    pageTypes: z
      .array(sightglassPageTypeSchema)
      .max(12)
      .optional()
      .describe("Pages: only these page types"),
    adTypes: z
      .array(sightglassAdTypeSchema)
      .max(3)
      .optional()
      .describe("Ads: only these formats"),
    hookTypes: z
      .array(sightglassHookTypeSchema)
      .max(20)
      .optional()
      .describe("Ads: only these hooks, from the confident reading"),
    angleTypes: z
      .array(sightglassAngleTypeSchema)
      .max(20)
      .optional()
      .describe("Ads: only these angles, from the confident reading"),
    offerTags: z
      .array(sightglassOfferTagSchema)
      .max(10)
      .optional()
      .describe("Ads: only ads carrying one of these offers"),
    categoryIds: z
      .array(z.uuid())
      .max(20)
      .optional()
      .describe("Companies: only companies in these categories or below them"),
    awareness: z
      .array(projectPersonaAwarenessSchema)
      .max(5)
      .optional()
      .describe("Personas: only these awareness stages"),
    ageBands: z
      .array(sightglassAgeBandSchema)
      .max(6)
      .optional()
      .describe("Personas: only these age bands"),
  })
  .strict()
  .describe("SightglassArenaFilters");
export type SightglassArenaFilters = z.infer<
  typeof sightglassArenaFiltersSchema
>;

export const sightglassArenaMapBodySchema = z
  .object({
    kind: sightglassArenaKindSchema,
    x: sightglassArenaPolesSchema,
    y: sightglassArenaPolesSchema,
    companyListId: z
      .uuid()
      .optional()
      .describe(
        "Scope to the companies on this company list; omit for the top companies",
      ),
    projectId: z
      .uuid()
      .optional()
      .describe(
        "Adds the project's own company and personas, flagged isYou; needs project access",
      ),
    filters: sightglassArenaFiltersSchema.optional(),
  })
  .strict()
  .describe("SightglassArenaMapBody");

export const sightglassArenaItemWireSchema = z
  .object({
    key: z.string().describe("kind:id, unique within the map"),
    kind: sightglassArenaKindSchema,
    id: z
      .string()
      .describe("The ad, page, persona or company id, for opening the item"),
    name: z.string(),
    subtitle: z.string().nullable(),
    companyId: z.string().nullable(),
    companyDomain: z.string().nullable(),
    logoUrl: z.string().nullable(),
    imageUrl: z
      .string()
      .nullable()
      .describe("A still for ads, a phone screenshot for pages"),
    avatarSvg: z.string().nullable().describe("Personas only"),
    url: z.string().nullable().describe("Pages only: the page's address"),
    pageType: sightglassPageTypeSchema
      .nullable()
      .describe("Pages only; null while the page is unclassified"),
    isYou: z
      .boolean()
      .describe("The project's own company, or one of its personas"),
    x: z
      .number()
      .describe(
        "Cosine similarity to the x axis; only the order is meaningful, so plot relative to the other items",
      ),
    y: z.number(),
  })
  .describe("SightglassArenaItemWire");

export const sightglassArenaMapWireSchema = z
  .object({ items: z.array(sightglassArenaItemWireSchema) })
  .describe("SightglassArenaMapWire");

export type SightglassArenaItemWire = z.infer<
  typeof sightglassArenaItemWireSchema
>;
export type SightglassArenaMapWire = z.infer<
  typeof sightglassArenaMapWireSchema
>;
